import { useEffect, useState } from "react";
import api from "../../services/axiosInstance";
import { faCrown, faRocket, faStar } from "@fortawesome/free-solid-svg-icons";

const PROTECTED_KEYS = ["color", "icon"];

export default function ServiceRoutes() {
  const [data, setData] = useState({});
  const [changes, setChanges] = useState({});
  const [loading, setLoading] = useState(false);

  // FETCH
  useEffect(() => {
    api.get("/services").then(res => setData(res.data));
  }, []);

  // SAFE CHANGE HANDLER (blocks protected keys)
 const handleChange = (path, value) => {
  if (PROTECTED_KEYS.includes(path[path.length - 1])) return;

  // 1️⃣ Update UI data
  setData(prev => {
    const copy = structuredClone(prev);
    let ref = copy;

    path.forEach((key, index) => {
      if (index === path.length - 1) {
        ref[key] = value;
      } else {
        ref = ref[key];
      }
    });

    return copy;
  });

  // 2️⃣ Track only changes for PATCH
  setChanges(prev => {
    const copy = structuredClone(prev);
    let ref = copy;

    path.forEach((key, index) => {
      if (index === path.length - 1) {
        ref[key] = value;
      } else {
        ref[key] = ref[key] || {};
        ref = ref[key];
      }
    });

    return copy;
  });
};


  // SAVE
  const saveChanges = async () => {
    if (!Object.keys(changes).length) return alert("No changes");

    setLoading(true);
    await api.patch("/services", changes, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    setLoading(false);
    setChanges({});
    alert("Updated successfully");
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 p-8">
      <h1 className="text-3xl font-bold mb-8">Admin Services Editor</h1>

      <div className="space-y-10">
        {Object.entries(data).map(([sectionKey, section]) => (
          <SectionEditor
            key={sectionKey}
            sectionKey={sectionKey}
            section={section}
            onChange={handleChange}
          />
        ))}
      </div>

      <button
        onClick={saveChanges}
        disabled={loading}
        className="fixed bottom-6 right-6 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold px-6 py-3 rounded-xl shadow-xl disabled:opacity-50"
      >
        {loading ? "Saving..." : "Save Changes"}
      </button>
    </div>
  );
}

/* ===================== SUB COMPONENTS ===================== */

function SectionEditor({ sectionKey, section, onChange }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <h2 className="text-2xl font-semibold capitalize mb-6">{sectionKey}</h2>

      <Input
        label="Title"
        value={section.title}
        onChange={v => onChange([sectionKey, "title"], v)}
      />

      <Textarea
        label="Description"
        value={section.description}
        onChange={v => onChange([sectionKey, "description"], v)}
      />

      <Input
        label="Pricing"
        value={section.pricing}
        onChange={v => onChange([sectionKey, "pricing"], v)}
      />

      <Input
        label="Starting Price"
        value={section.startingPrice}
        onChange={v => onChange([sectionKey, "startingPrice"], v)}
      />

      <ListEditor
        label="Highlights"
        items={section.highlights || []}
        onChange={items => onChange([sectionKey, "highlights"], items)}
      />

      <ListEditor
        label="Services"
        items={section.services}
        onChange={items => onChange([sectionKey, "services"], items)}
      />

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {section.packs.map((pack, i) => (
          <PackEditor
            key={i}
            pack={pack}
            sectionKey={sectionKey}
            index={i}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
}


function PackEditor({ pack, sectionKey, index, onChange }) {
  return (
    <div className="bg-gray-50 border border-gray-700 rounded-xl p-5">
      <Input
        label="Pack Name"
        value={pack.name}
        onChange={v =>
          onChange([sectionKey, "packs", index, "name"], v)
        }
      />

      <Input
        label="Price"
        value={pack.price}
        onChange={v =>
          onChange([sectionKey, "packs", index, "price"], v)
        }
      />

      <Textarea
        label="Description"
        value={pack.description}
        onChange={v =>
          onChange([sectionKey, "packs", index, "description"], v)
        }
      />

      <ListEditor
        label="Features"
        items={pack.features}
        onChange={items =>
          onChange([sectionKey, "packs", index, "features"], items)
        }
      />

     
    </div>
  );
}

/* ===================== UI HELPERS ===================== */

function Input({ label, value, onChange }) {
  return (
    <div className="mb-4">
      <label className="text-sm text-gray-500">{label}</label>
      <input
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-gray-50 border border-gray-700 rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-emerald-500"
      />
    </div>
  );
}

function Textarea({ label, value, onChange }) {
  return (
    <div className="mb-4">
      <label className="text-sm text-gray-500">{label}</label>
      <textarea
        rows={3}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-gray-50 border border-gray-700 rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-emerald-500"
      />
    </div>
  );
}

function ListEditor({ label, items, onChange }) {
  const updateItem = (i, val) => {
    const copy = [...items];
    copy[i] = val;
    onChange(copy);
  };

  return (
    <div className="mb-4">
      <label className="text-sm text-gray-500">{label}</label>
      {items.map((item, i) => (
        <input
          key={i}
          value={item}
          onChange={e => updateItem(i, e.target.value)}
          className="w-full bg-gray-50 border border-gray-700 rounded-lg px-3 py-2 mt-2"
        />
      ))}
    </div>
  );
}
